import { env } from '@/config/env';
import { AccessControlGate } from '@/domains/access-control';
import { queryClient } from '@/lib/query-client';
import { ErrorBoundary, ThemeProvider } from '@/shared/components';
import { Toaster } from '@/shared/ui/toaster';
import { TooltipProvider } from '@/shared/ui/tooltip';
import { QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy } from 'react';
import type React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { BrowserRouter } from 'react-router';

// Lazy load React Query Devtools only in development
// This ensures the devtools code is completely tree-shaken in production
const ReactQueryDevtools =
  env.isDevelopment && env.performance.enableReactQueryDevtools
    ? lazy(() =>
        import('@tanstack/react-query-devtools').then((module) => ({
          default: module.ReactQueryDevtools,
        })),
      )
    : null;

interface AppProvidersProps {
  children: React.ReactNode;
}

/**
 * AppProviders - Wraps the application with all necessary context providers
 *
 * Provider order:
 * 1. ErrorBoundary - Catches React errors at top level
 * 2. QueryClientProvider - Provides React Query client
 * 3. BrowserRouter - Provides routing context
 * 4. ThemeProvider - Provides theme context
 * 5. TooltipProvider - Provides shared tooltip context
 * 6. AccessControlGate - Hydrates the app-wide CASL ability (deny-all until loaded)
 * 7. ReactQueryDevtools - Dev-only overlay (sibling to router, not child)
 *
 * The devtools are lazily loaded only in development mode when
 * VITE_ENABLE_REACT_QUERY_DEVTOOLS is true (default in dev).
 * Rendered as a sibling to <BrowserRouter> (not inside it) because devtools
 * only need QueryClientProvider context. Placed at the end for a clean tree
 * structure, keeping overlay components after the main application tree.
 */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
          <DndProvider backend={HTML5Backend}>
            <ThemeProvider>
              <TooltipProvider delayDuration={150}>
                <AccessControlGate>
                  {children}
                  <Toaster />
                </AccessControlGate>
              </TooltipProvider>
            </ThemeProvider>
          </DndProvider>
        </ErrorBoundary>
      </BrowserRouter>
      {ReactQueryDevtools && (
        <Suspense fallback={null}>
          <ReactQueryDevtools
            initialIsOpen={false}
            buttonPosition="bottom-left"
            position="bottom"
          />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}
